/**
 * useExtraWork — доп.работы for a day (B.12-R8.7). GET /api/extra-work?date= +
 * create/remove. Non-game OO-assigned work shown inline in the day feed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";
import { fmtDateApi } from "../utils/date";
import { sendExtraWork } from "./extraWorkSend";

export interface ExtraWork {
  id: number;
  /** YYYY-MM-DD (10.10: окно проверки допработ за период). */
  date: string;
  operatorId: string;
  operatorName: string;
  /** 10.10: оператор по роли — его считает зарплата; ДК/ОО — нет (нет поля — считаем оператором). */
  operatorPaid?: boolean;
  timeFrom: string;
  timeTo: string;
  description: string;
  branchId: string | null;
  /** Ставка ₽/час (rate_override приоритетнее), такси и статус — для вкладки «Доп» оператора (23.08). */
  rate: number;
  /** Чистая ставка ₽/ч без оверрайда суммы (28.08: ОО правит их раздельно). */
  hourlyRate: number;
  taxi: number;
  /** Кто оплатил такси (11.09): пусто — исполнитель, иначе имя того, кому вернуть. */
  taxiPayerName: string | null;
  /** Итоговая сумма, заданная ОО вручную (null — считаем по часам). */
  rate_override: number | null;
  /** Момент, когда исполнитель отчитался о работе (11.09) — null, пока не отчитался. */
  reportedAt: string | null;
  /** Из отчёта (11.09): что делал и фактическое время работы. */
  reportText: string | null;
  factFrom: string | null;
  factTo: string | null;
  /** ОО подтвердил фактическое время (11.09) — до этого часы считаются по плану. */
  factOk: boolean;
  /** staffId плательщика такси — нужен, чтобы отчёт открывался с выбранным человеком. */
  taxiPaidBy: string | null;
  status: string;
  /** 26.09 (владелец): учёбка. 'training' — ученик (без оплаты), 'training_curator' —
   *  куратор (обычная оплачиваемая допработа), null — обычная допработа. */
  kind: WorkKind | null;
  /** Ученику — кто проводит учёбку (сервер находит куратора того же слота). */
  curatorName: string | null;
  /** Оператор подтвердил, что видел допработу (26.08, владелец). */
  accepted: boolean;
  /** 26.09: сохранено, но не отправлено — оператор не видит, пока ОО не нажмёт «Отправить». */
  draft: boolean;
}

/** One доп.работа (time + branch + description) with all its operators. */
export interface ExtraWorkGroup {
  key: string;
  /** 26.09: карточка учёбки (ученики + куратор), а не допработы. */
  training: boolean;
  /** 26.09: карточка сохранена, но не отправлена — на доске «не отправлено» и кнопка отправки. */
  draft: boolean;
  timeFrom: string;
  timeTo: string;
  description: string;
  branchId: string | null;
  /** rate — ставка ₽/ч строки (27.09: у куратора её ставят удержанием чипа на доске). */
  ops: { rowId: number; operatorId: string; operatorName: string; accepted: boolean; kind: WorkKind | null; rate: number }[];
  /** Такси по отчётам исполнителей (11.09): сумма и кто его заявил — ОО должен видеть деньги. */
  taxi: number;
  taxiWho: string[];
  /** Отчёты, где фактическое время разошлось с планом и ждёт подтверждения ОО. */
  factPending: { rowId: number; who: string; from: string; to: string; text: string | null }[];
}

export interface NewExtraWork {
  operator_id: string;
  date: string;
  time_from: string;
  time_to: string;
  description: string;
  branch_id?: string;
  /** 26.09: учёбка. Добавление человека в карточку учёбки ОБЯЗАНО нести kind —
   *  иначе он получит оплачиваемую допработу вместо учёбки. */
  kind?: WorkKind;
  curator_id?: string; /** 19.10: false — только сохранить (черновик), сообщение — по «Отправить» */ notify?: boolean;
}

/** Shared fields for a multi-operator доп.работа. */
export interface NewExtraWorkBase {
  date: string;
  time_from: string;
  time_to: string;
  description: string;
  branch_id?: string;
  kind?: WorkKind;
  curator_id?: string;
  /** 26.09: false — «Сохранить» без Telegram (как галочка на доске). */
  notify?: boolean;
}

export interface RawEw {
  id: number; date?: string; operator_id: string; operator_name?: string; time_from?: string;
  time_to?: string; description?: string; branch_id?: string | null;
  hourly_rate?: number | null; rate_override?: number | null; taxi?: number | null; status?: string | null;
  accepted_at?: string | null; taxi_payer_name?: string | null; reported_at?: string | null;
  report_text?: string | null; fact_from?: string | null; fact_to?: string | null; taxi_paid_by?: string | null;
  fact_ok?: number | null;
  kind?: string | null; operator_paid?: number | null;
  curator_name?: string | null;
  draft?: number | null;
}

export type WorkKind = "training" | "training_curator";
const asKind = (v: unknown): WorkKind | null => (v === "training" || v === "training_curator" ? v : null);

/** Строка API → ExtraWork (день на доске и история оператора — одно сопоставление). */
export function toExtraWork(e: RawEw): ExtraWork {
  return {
    id: e.id,
    date: String(e.date ?? "").slice(0, 10),
    operatorId: String(e.operator_id),
    operatorName: e.operator_name ?? String(e.operator_id), operatorPaid: e.operator_paid == null ? undefined : Number(e.operator_paid) === 1,
    timeFrom: (e.time_from ?? "").slice(0, 5),
    timeTo: (e.time_to ?? "").slice(0, 5),
    description: e.description ?? "",
    branchId: e.branch_id ?? null,
    rate: Number(e.rate_override ?? e.hourly_rate ?? 0),
    hourlyRate: Number(e.hourly_rate ?? 0),
    taxi: Number(e.taxi ?? 0),
    rate_override: e.rate_override ?? null,
    taxiPayerName: e.taxi_payer_name ?? null,
    reportedAt: e.reported_at ?? null,
    reportText: e.report_text ?? null,
    factFrom: e.fact_from ? String(e.fact_from).slice(0, 5) : null,
    factTo: e.fact_to ? String(e.fact_to).slice(0, 5) : null,
    taxiPaidBy: e.taxi_paid_by ?? null,
    factOk: !!e.fact_ok,
    status: e.status ?? "pending",
    kind: asKind(e.kind),
    curatorName: e.curator_name ?? null,
    accepted: !!e.accepted_at,
    draft: Number(e.draft) === 1,
  };
}

export function useExtraWork(date: Date) {
  const [items, setItems] = useState<ExtraWork[]>([]);
  const [loading, setLoading] = useState(true);
  const [trigger, setTrigger] = useState(0);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api<ApiList<RawEw>>(`/api/extra-work?date=${fmtDateApi(date)}`)
      .then((r) => {
        if (!alive) return;
        setItems((r.results ?? []).map(toExtraWork));
        setLoading(false);
      })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date, trigger]);

  const create = useCallback(async (w: NewExtraWork): Promise<boolean> => {
    try {
      await api("/api/extra-work", { method: "POST", body: w });
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  /** Create one extra_work row per operator (multi-operator доп.работа).
   *  Returns the server error text on failure so the sheet can show it. */
  const createMany = useCallback(async (operatorIds: string[], base: NewExtraWorkBase): Promise<{ ok: boolean; error?: string; warn?: string }> => {
    if (operatorIds.length === 0) return { ok: false, error: "Не выбраны операторы" };
    try {
      /* 09.09 (владелец: «людям не дошли уведомления о допработах»): сервер теперь говорит,
         ушло ли сообщение каждому. Молчать об этом нельзя — работа назначена, а человек не знает. */
      const res = await Promise.all(operatorIds.map((operator_id) =>
        api<{ ok: boolean; notified?: boolean | null }>("/api/extra-work", { method: "POST", body: { ...base, operator_id } })
          .then((r) => ({ operator_id, notified: r?.notified })),
      ));
      reload();
      const silent = res.filter((r) => r.notified === false).length;
      if (silent) return { ok: true, warn: `Назначено, но уведомление не дошло: ${silent} из ${res.length}. Скажите им лично.` };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Не удалось создать" };
    }
  }, [reload]);

  /** cancel — учёбка: отправленной человек получит «Учёбка отменена» (27.09, владелец). */
  const remove = useCallback(async (id: number, cancel = false) => {
    await api(`/api/extra-work/${id}${cancel ? "?notify=cancel" : ""}`, { method: "DELETE" }).catch(() => {});
    reload();
  }, [reload]);

  /** Group rows by time+branch+description so one доп.работа shows its crew. */
  const groups = useMemo<ExtraWorkGroup[]>(() => {
    const map = new Map<string, ExtraWorkGroup>();
    for (const e of items) {
      /* 26.09: учёбка — своя карточка, даже при совпадении времени и описания с допработой;
         куратор (training_curator) идёт в ту же карточку, что и его ученики. */
      const training = e.kind === "training" || e.kind === "training_curator";
      /* 26.09: сохранённое и отправленное — разные карточки: иначе «Отправить» задело бы
         уже отправленных, а «не отправлено» висело бы над людьми, которым уже написали. */
      const key = `${training ? "t" : "w"}${e.draft ? "d" : ""}|${e.timeFrom}|${e.timeTo}|${e.branchId ?? ""}|${e.description}`;
      const g = map.get(key) ?? { key, training, draft: e.draft, timeFrom: e.timeFrom, timeTo: e.timeTo, description: e.description, branchId: e.branchId, ops: [], taxi: 0, taxiWho: [], factPending: [] };
      g.ops.push({ rowId: e.id, operatorId: e.operatorId, operatorName: e.operatorName, accepted: e.accepted, kind: e.kind, rate: e.hourlyRate });
      /* Каждый исполнитель отчитывается за свою поездку — складываем и показываем, кто заявил. */
      if (e.taxi > 0) { g.taxi += e.taxi; g.taxiWho.push(e.taxiPayerName ?? e.operatorName); }
      if (!e.factOk && e.factFrom && e.factTo && (e.factFrom !== e.timeFrom || e.factTo !== e.timeTo)) {
        g.factPending.push({ rowId: e.id, who: e.operatorName, from: e.factFrom, to: e.factTo, text: e.reportText });
      }
      map.set(key, g);
    }
    return Array.from(map.values());
  }, [items]);

  /** 26.09: «Отправить» сохранённую карточку (extraWorkSend.ts). */
  const send = useCallback((rowIds: number[]) => sendExtraWork(rowIds).finally(reload), [reload]);

  return { items, groups, loading, reload, create, createMany, remove, send };
}

/** Вся история допработ оператора (26.08, владелец): GET без date — сервер для
 *  роли operator отдаёт только его строки; свежие сверху. Ленивая (enabled). */
export interface ExtraWorkHist extends ExtraWork { date: string }

export function useExtraWorkHistory(enabled: boolean): { items: ExtraWorkHist[]; loading: boolean } {
  const [items, setItems] = useState<ExtraWorkHist[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    api<ApiList<RawEw & { date?: string }>>("/api/extra-work")
      .then((r) => {
        if (!alive) return;
        const mapped: ExtraWorkHist[] = (r.results ?? []).map((e) => ({ ...toExtraWork(e), date: (e.date ?? "").slice(0, 10) }));
        mapped.sort((a, b) => b.date.localeCompare(a.date) || a.timeFrom.localeCompare(b.timeFrom));
        setItems(mapped); setLoading(false);
      })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [enabled]);
  return { items, loading };
}

